import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBackgroundMusic } from "@/context/BackgroundMusicContext";
import { toast } from "sonner";
import { Upload, Lock, Unlock, Save, Music, KeyRound, Trash2, Scissors, Merge, Rocket, Clock, Eye, EyeOff } from "lucide-react";
import StatsPanel from "@/components/StatsPanel";
import BroadcastsPanel from "@/components/BroadcastsPanel";
import DailyRevenueEmailPanel from "@/components/DailyRevenueEmailPanel";
import CheckoutFailuresPanel from "@/components/CheckoutFailuresPanel";
import AccessCodesPanel from "@/components/AccessCodesPanel";
import EvidenceStatsPanel from "@/components/EvidenceStatsPanel";
import PurgeUsersPanel from "@/components/PurgeUsersPanel";
import TopicAdminPanel from "@/components/TopicAdminPanel";

function BackgroundMusicPanel() {
  const { refresh } = useBackgroundMusic();
  const [current, setCurrent] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site/background-music");
      setCurrent(data?.url || null);
    } catch { /* ignored */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { toast.error("pick a music file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/background-music", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("background music uploaded");
      setFile(null);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "upload failed");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove background music from the site?")) return;
    try {
      await api.delete("/admin/background-music");
      toast.success("background music removed");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "remove failed");
    }
  };

  return (
    <div className="bg-coal border border-hotpink/30 p-5 md:p-6 mb-10" data-testid="bg-music-panel">
      <div className="flex items-center gap-2 mb-2">
        <Music size={16} className="text-hotpink" />
        <p className="font-outfit font-extrabold text-white uppercase tracking-widest text-xs">Site background music</p>
      </div>
      <p className="font-outfit text-white/60 text-sm mb-4">Plays softly on every page. Auto-pauses when someone presses play on an episode. Visitors can mute or skip it from the header or the floating button.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept="audio/mpeg,audio/wav,audio/mp4,audio/ogg,.mp3,.wav,.m4a,.ogg"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="bg-music-file"
          className="text-xs text-white/70 file:bg-hotpink file:text-white file:font-bold file:uppercase file:text-[10px] file:tracking-widest file:px-3 file:py-1 file:border-0 file:mr-2 file:cursor-pointer"
        />
        <button
          onClick={upload}
          disabled={uploading || !file}
          data-testid="bg-music-upload"
          className="bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-[10px] px-4 py-2 transition-colors flex items-center gap-1 disabled:opacity-40"
        >
          <Upload size={12} /> {uploading ? "uploading..." : "upload music"}
        </button>
        {current && (
          <>
            <span className="text-white/70 text-xs font-outfit" data-testid="bg-music-current">Currently set ✓</span>
            <button
              onClick={remove}
              data-testid="bg-music-remove"
              className="text-white/50 hover:text-hotpink transition-colors flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold"
            >
              <Trash2 size={12} /> remove
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("new password must be at least 6 characters");
    if (next !== confirm) return toast.error("passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("password updated ♡");
      setCur(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-coal border border-hotpink/40 p-5 md:p-6 mb-8" data-testid="admin-password-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <KeyRound className="text-hotpink" size={22}/>
          <div>
            <h3 className="font-outfit font-bold text-white text-lg">Change admin password</h3>
            <p className="scribble text-hotpink text-lg">keep the diary on lock 🔒</p>
          </div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="admin-change-password-toggle" className="text-xs uppercase tracking-widest font-outfit font-bold text-white border border-white/30 hover:border-hotpink hover:text-hotpink px-4 py-2 transition-colors">
            change password
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 grid md:grid-cols-3 gap-3" data-testid="admin-password-form">
          <input type="password" required placeholder="current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="admin-pw-current" className="bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
          <input type="password" required minLength={6} placeholder="new password (min 6)" value={next} onChange={(e) => setNext(e.target.value)} data-testid="admin-pw-new" className="bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
          <input type="password" required placeholder="confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="admin-pw-confirm" className="bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
          <div className="md:col-span-3 flex gap-3 items-center">
            <button type="submit" disabled={busy} data-testid="admin-pw-submit" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-5 py-2 disabled:opacity-60">
              {busy ? "..." : "update password"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setCur(""); setNext(""); setConfirm(""); }} className="text-white/60 text-xs uppercase tracking-widest hover:text-hotpink">cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

function EpisodeRow({ ep, hasPt2 = false, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: ep.title,
    description: ep.description,
    recap_note: ep.recap_note,
    duration_label: ep.duration_label,
    is_free: ep.is_free,
  });
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const isComingSoon = ep.status === "coming_soon";
  const [releaseDate, setReleaseDate] = useState(ep.release_date ? ep.release_date.slice(0, 10) : "");

  useEffect(() => {
    setReleaseDate(ep.release_date ? ep.release_date.slice(0, 10) : "");
  }, [ep.release_date]);

  useEffect(() => {
    setForm({
      title: ep.title,
      description: ep.description,
      recap_note: ep.recap_note,
      duration_label: ep.duration_label,
      is_free: ep.is_free,
    });
  }, [ep.title, ep.description, ep.recap_note, ep.duration_label, ep.is_free]);

  const toggleStatus = async () => {
    setStatusBusy(true);
    try {
      await api.patch(`/admin/episodes/${ep.number}`, {
        status: isComingSoon ? "released" : "coming_soon",
      });
      toast.success(isComingSoon ? `ep ${ep.number} is now RELEASED` : `ep ${ep.number} marked COMING SOON`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "status change failed");
    } finally { setStatusBusy(false); }
  };

  const saveReleaseDate = async () => {
    setStatusBusy(true);
    try {
      await api.patch(`/admin/episodes/${ep.number}`, {
        release_date: releaseDate || null,
      });
      toast.success(releaseDate ? `release date saved` : `release date cleared`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "date save failed");
    } finally { setStatusBusy(false); }
  };

  const saveEvidence = async () => {
    if (!evidenceFile) { toast.error("pick an image first"); return; }
    setEvidenceBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", evidenceFile);
      await api.post(`/admin/episodes/${ep.number}/evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`evidence locker saved for ep ${ep.number}`);
      setEvidenceFile(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "upload failed");
    } finally { setEvidenceBusy(false); }
  };

  const removeEvidence = async () => {
    if (!window.confirm("Remove this episode's evidence locker image (and the IG/TikTok variants)?")) return;
    setEvidenceBusy(true);
    try {
      await api.delete(`/admin/episodes/${ep.number}/evidence`);
      toast.success(`evidence locker removed for ep ${ep.number}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "remove failed");
    } finally { setEvidenceBusy(false); }
  };

  const split = async () => {
    if (!window.confirm(`Split Episode ${ep.number} into Pt. 1 + Pt. 2? A new sibling will be created with placeholder audio you can replace later.`)) return;
    setSplitting(true);
    try {
      await api.post(`/admin/episodes/${ep.number}/split`);
      toast.success(`ep ${ep.number} split into 2 parts`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "split failed");
    } finally { setSplitting(false); }
  };

  const unsplit = async () => {
    if (!window.confirm(`Remove Pt. 2 of Episode ${ep.parent_number || ep.number}?\n\nPt. 2's plays, comments, ratings, and uploaded audio will be deleted. Pt. 1 will keep its "— Pt. 1" label — you can add Pt. 2 back later or rename Pt. 1 manually.`)) return;
    setSplitting(true);
    try {
      await api.post(`/admin/episodes/${ep.number}/unsplit`);
      toast.success(`Pt. 2 of ep ${ep.number} removed`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "merge failed");
    } finally { setSplitting(false); }
  };

  const toggleVisibility = async () => {
    const nextHidden = !ep.is_hidden;
    const confirmMsg = nextHidden
      ? `Hide Episode ${ep.number} from the public listing? Listeners won't see it until you 'release' it again.`
      : `Release Episode ${ep.number} — this will show it on the public /episodes page immediately.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/admin/episodes/${ep.number}/visibility`, { is_hidden: nextHidden });
      toast.success(nextHidden ? `ep ${ep.number} hidden` : `ep ${ep.number} released ♡`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "update failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/episodes/${ep.number}`, form);
      toast.success(`ep ${ep.number} saved`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally { setSaving(false); }
  };

  const upload = async () => {
    if (!file) { toast.error("pick an audio file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/episodes/${ep.number}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`uploaded audio for ep ${ep.number}`);
      setFile(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "upload failed");
    } finally { setUploading(false); }
  };

  const hasUploadedAudio = (ep.audio_url || "").startsWith("/api/audio/");

  return (
    <div className="bg-coal border border-white/10 p-5" data-testid={`admin-episode-${ep.number}`}>
      <div className="flex items-start gap-5">
        <div className="flex flex-col items-center min-w-[60px]">
          <span className="scribble text-white/40 text-xs uppercase">Ep</span>
          <span className="brush-title text-5xl text-white leading-none">{String(ep.parent_number || ep.number).padStart(2, "0")}</span>
          {ep.part && (
            <span className="scribble text-hotpink text-xs mt-1">pt. {ep.part}</span>
          )}
          {ep.is_free ? (
            <span className="mt-2 text-[10px] uppercase tracking-widest text-green-400 flex items-center gap-1"><Unlock size={10}/> free</span>
          ) : (
            <span className="mt-2 text-[10px] uppercase tracking-widest text-hotpink flex items-center gap-1"><Lock size={10}/> $1</span>
          )}
        </div>

        <div className="flex-1">
          {!editing ? (
            <>
              <h3 className="font-outfit font-bold text-xl text-white">{ep.title}</h3>
              <p className="text-white/60 text-sm mt-1">{ep.description}</p>
              <p className="scribble text-hotpink text-base mt-1">{ep.recap_note}</p>
              <p className="text-white/40 text-xs uppercase tracking-widest mt-2">
                {ep.duration_label}
                {ep.part && (
                  <span className="ml-2 text-hotpink">· Pt. {ep.part}</span>
                )}
              </p>
              {ep.evidence_images?.original && (
                <p className="text-hotpink/80 text-[10px] uppercase tracking-widest mt-1" data-testid={`admin-has-evidence-${ep.number}`}>♥ evidence image set</p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <button onClick={() => setEditing(true)} data-testid={`admin-edit-${ep.number}`} className="text-xs uppercase tracking-widest text-hotpink hover:underline">edit text →</button>
                {ep.kind !== "recap" && !ep.part && (
                  <button
                    onClick={split}
                    disabled={splitting}
                    data-testid={`admin-split-${ep.number}`}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-white/60 hover:text-hotpink transition-colors disabled:opacity-60"
                  >
                    <Scissors size={11} /> {splitting ? "..." : "split into 2 parts"}
                  </button>
                )}
                {ep.part === 1 && hasPt2 && (
                  <button
                    onClick={unsplit}
                    disabled={splitting}
                    data-testid={`admin-unsplit-${ep.number}`}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-white/60 hover:text-hotpink transition-colors disabled:opacity-60"
                  >
                    <Merge size={11} /> {splitting ? "..." : "remove Pt. 2"}
                  </button>
                )}
                {ep.part === 1 && !hasPt2 && (
                  <button
                    onClick={split}
                    disabled={splitting}
                    data-testid={`admin-add-pt2-${ep.number}`}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-white/60 hover:text-hotpink transition-colors disabled:opacity-60"
                  >
                    <Scissors size={11} /> {splitting ? "..." : "add Pt. 2 back"}
                  </button>
                )}
                {ep.part === 2 && (
                  <span className="text-[10px] uppercase tracking-widest text-white/30" data-testid={`admin-pt2-managed-${ep.number}`}>
                    managed via Pt. 1
                  </span>
                )}
                {ep.part !== 2 && (
                  <button
                    onClick={toggleVisibility}
                    data-testid={`admin-visibility-${ep.number}`}
                    className={`flex items-center gap-1 font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-1 transition-colors ${
                      ep.is_hidden
                        ? "bg-yellow-500/20 border border-yellow-500/60 text-yellow-300 hover:bg-yellow-500/30"
                        : "border border-white/20 text-white/60 hover:border-hotpink hover:text-hotpink"
                    }`}
                  >
                    {ep.is_hidden ? <><EyeOff size={11}/> hidden — release</> : <><Eye size={11}/> hide</>}
                  </button>
                )}
              </div>

              {/* Status control — Coming Soon vs Released + optional release date */}
              <div className="mt-3 bg-ink border border-white/10 p-3 flex flex-wrap items-center gap-3" data-testid={`admin-status-panel-${ep.number}`}>
                <button
                  onClick={toggleStatus}
                  disabled={statusBusy}
                  data-testid={`admin-status-toggle-${ep.number}`}
                  className={`flex items-center gap-1 font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-1.5 transition-colors disabled:opacity-50 ${
                    isComingSoon
                      ? "bg-white text-ink hover:bg-hotpink hover:text-white"
                      : "border border-white/20 text-white/70 hover:border-hotpink hover:text-hotpink"
                  }`}
                >
                  {isComingSoon ? <><Rocket size={11}/> release now</> : <><Clock size={11}/> mark coming soon</>}
                </button>
                <span
                  className={`text-[10px] uppercase tracking-widest ${isComingSoon ? "text-hotpink" : "text-green-400"}`}
                  data-testid={`admin-status-label-${ep.number}`}
                >
                  {isComingSoon ? "○ coming soon" : "● released"}
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">release date:</label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    data-testid={`admin-release-date-${ep.number}`}
                    className="bg-coal border border-white/15 px-2 py-1 text-white font-outfit text-xs focus:border-hotpink focus:outline-none"
                  />
                  <button
                    onClick={saveReleaseDate}
                    disabled={statusBusy}
                    data-testid={`admin-save-date-${ep.number}`}
                    className="text-white/60 hover:text-hotpink font-outfit font-bold uppercase tracking-widest text-[10px] px-2 py-1 disabled:opacity-50"
                  >
                    save
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-white/50 text-[10px] uppercase tracking-widest font-outfit block mb-1">Title</label>
                <input data-testid={`admin-title-${ep.number}`} value={form.title || ""} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Title (e.g. Mr. Right For Me — Pt. 1)" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-base focus:border-hotpink focus:outline-none" />
                <p className="text-white/30 text-[10px] mt-1">Free-form. Add or remove &ldquo;— Pt. 1&rdquo; / &ldquo;— Pt. 2&rdquo; as you like.</p>
              </div>
              <div>
                <label className="text-white/50 text-[10px] uppercase tracking-widest font-outfit block mb-1">Description</label>
                <textarea data-testid={`admin-desc-${ep.number}`} value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Full episode description. Write as much as you want — it wraps." rows={6} className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm leading-relaxed focus:border-hotpink focus:outline-none resize-y" />
                <p className="text-white/30 text-[10px] mt-1">{(form.description || "").length} chars · free-form</p>
              </div>
              <div>
                <label className="text-white/50 text-[10px] uppercase tracking-widest font-outfit block mb-1">Sticky-note recap</label>
                <input data-testid={`admin-recap-${ep.number}`} value={form.recap_note || ""} onChange={(e) => setForm({...form, recap_note: e.target.value})} placeholder="Short handwritten-style note shown on the sticky note" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-white/50 text-[10px] uppercase tracking-widest">Duration</label>
                  <input value={form.duration_label || ""} onChange={(e) => setForm({...form, duration_label: e.target.value})} placeholder="MM:SS" className="bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm w-24 focus:border-hotpink focus:outline-none" />
                </div>
                <label className="flex items-center gap-2 text-white/80 font-outfit text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.is_free} onChange={(e) => setForm({...form, is_free: e.target.checked})} className="accent-hotpink" /> Free
                </label>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => setEditing(false)} className="text-white/60 text-xs uppercase tracking-widest hover:text-hotpink" data-testid={`admin-cancel-${ep.number}`}>cancel</button>
                  <button disabled={saving} onClick={save} data-testid={`admin-save-${ep.number}`} className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-bold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60 flex items-center gap-1">
                    <Save size={12}/> {saving ? "..." : "save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Joey's Evidence Locker — image upload, independent of the main edit form */}
          <div className="mt-4 bg-ink border-l-4 border-hotpink p-3" data-testid={`admin-evidence-panel-${ep.number}`}>
            <p className="scribble text-hotpink text-base rotate-[-1deg] inline-block leading-none mb-2">joey&rsquo;s evidence locker</p>
            <p className="font-outfit text-white/50 text-[10px] uppercase tracking-widest mb-3">image unlocks for listeners after they play this episode</p>
            {ep.evidence_images?.original && (
              <img
                src={(ep.evidence_images.original.startsWith("http") ? "" : (process.env.REACT_APP_BACKEND_URL || "")) + ep.evidence_images.original}
                alt="current evidence"
                data-testid={`admin-evidence-thumb-${ep.number}`}
                className="block max-h-32 max-w-full object-contain bg-coal border border-white/10 mb-3"
              />
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
              data-testid={`admin-evidence-file-${ep.number}`}
              className="w-full text-xs text-white/70 mb-2 file:bg-hotpink file:text-white file:font-bold file:uppercase file:text-[10px] file:tracking-widest file:px-3 file:py-1 file:border-0 file:mr-2 file:cursor-pointer"
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={saveEvidence}
                disabled={evidenceBusy || !evidenceFile}
                data-testid={`admin-evidence-save-${ep.number}`}
                className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 disabled:opacity-50 flex items-center gap-1"
              >
                <Upload size={11}/> {evidenceBusy ? "..." : (ep.evidence_images?.original ? "replace image" : "upload image")}
              </button>
              {ep.evidence_images?.original && (
                <button
                  onClick={removeEvidence}
                  disabled={evidenceBusy}
                  data-testid={`admin-evidence-remove-${ep.number}`}
                  className="border border-white/20 hover:border-hotpink text-white/60 hover:text-hotpink font-outfit font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 disabled:opacity-50 flex items-center gap-1"
                >
                  <Trash2 size={11}/> remove
                </button>
              )}
              <span className="text-[10px] uppercase tracking-widest text-white/30 ml-auto">
                {ep.evidence_images?.original ? "image + 2 variants ♥" : "empty"}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-72 bg-ink border border-hotpink/30 p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1"><Music size={11}/> Episode audio</div>
          <div className="flex items-center justify-between text-xs font-outfit mb-3">
            <span className="text-white/80">MP3 file</span>
            <span className={hasUploadedAudio ? "text-green-400" : "text-white/30"} data-testid={`admin-audio-status-${ep.number}`}>
              {hasUploadedAudio ? "uploaded" : "placeholder"}
            </span>
          </div>
          <input
            type="file"
            accept="audio/mpeg,audio/wav,audio/mp4,audio/ogg,.mp3,.wav,.m4a,.ogg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid={`admin-file-${ep.number}`}
            className="w-full text-xs text-white/70 mb-2 file:bg-hotpink file:text-white file:font-bold file:uppercase file:text-[10px] file:tracking-widest file:px-3 file:py-1 file:border-0 file:mr-2 file:cursor-pointer"
          />
          <button
            onClick={upload}
            disabled={uploading || !file}
            data-testid={`admin-upload-${ep.number}`}
            className="w-full bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-2 transition-colors flex items-center justify-center gap-1 disabled:opacity-40"
          >
            <Upload size={12}/> {uploading ? "uploading..." : "upload audio"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkHideAbove({ onDone }) {
  const [threshold, setThreshold] = useState("11");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const n = parseInt(threshold, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error("enter a valid episode number"); return; }
    if (!window.confirm(`Hide every non-recap episode with number > ${n}? Titles and audio are preserved — you can 'release' each one later, one by one.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/episodes/hide-above/${n}`);
      toast.success(`${data.regular_hidden} eps + ${data.pt2_hidden} Pt.2 rows hidden`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "bulk hide failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mb-6 bg-coal border border-hotpink/40 p-4 flex flex-wrap items-center gap-3" data-testid="bulk-hide-panel">
      <EyeOff className="text-hotpink" size={18} />
      <span className="font-outfit text-white/80 text-sm">Hide everything after ep</span>
      <input value={threshold} onChange={(e) => setThreshold(e.target.value)} type="number" min="0" max="999" data-testid="bulk-hide-threshold" className="w-20 bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none"/>
      <button onClick={run} disabled={busy} data-testid="bulk-hide-run" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60">
        {busy ? "..." : "hide 'em"}
      </button>
      <span className="text-white/40 text-xs font-outfit ml-auto">preserves titles & audio · release later, one by one ↓</span>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [eps, setEps] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [envFlags, setEnvFlags] = useState(null);

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/admin/episodes");
      setEps(data);
    } catch (e) {
      toast.error("admin access only");
    } finally { setFetching(false); }
  };

  const loadEnvFlags = async () => {
    try {
      const { data } = await api.get("/admin/env-flags", { params: { _: Date.now() } });
      setEnvFlags(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { nav("/"); return; }
    load();
    loadEnvFlags();
  }, [user, loading, nav]);

  if (loading || !user) return null;
  if (!user.is_admin) return null;

  return (
    <div className="bg-ink min-h-screen has-player" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
        <p className="scribble text-hotpink text-2xl rotate-[-2deg] inline-block mb-3">behind the curtain</p>
        <h1 className="brush-title text-5xl md:text-7xl text-white mb-3">ADMIN<span className="text-hotpink">.</span></h1>
        <p className="font-outfit text-white/60 max-w-2xl mb-10">Upload the real MP3 for each episode. Edit titles, descriptions, recaps, duration. Toggle free vs $1.</p>

        {envFlags?.stripe_test_mode && (
          <div className="mb-8 bg-yellow-500/10 border-2 border-dashed border-yellow-500/60 px-5 py-4 flex items-start gap-3" data-testid="stripe-test-banner">
            <div className="text-yellow-400 font-outfit font-extrabold uppercase tracking-widest text-xs flex-shrink-0 mt-0.5">TEST MODE</div>
            <div className="text-white/80 font-outfit text-sm leading-relaxed">
              Stripe is running in <span className="text-yellow-300 font-bold">test mode</span> — checkouts will succeed but no real money moves. Use card <span className="font-mono text-yellow-300">4242 4242 4242 4242</span> · any future expiry · any 3-digit CVC · any ZIP.
              <br className="hidden md:block"/>
              <span className="text-white/50 text-xs">Swap <span className="font-mono">STRIPE_API_KEY</span> to a live key (starts with <span className="font-mono">sk_live_</span>) once your Stripe account is verified.</span>
            </div>
          </div>
        )}

        <StatsPanel />

        <DailyRevenueEmailPanel />

        <EvidenceStatsPanel />

        <BackgroundMusicPanel />

        <TopicAdminPanel />

        <BroadcastsPanel />
        <AccessCodesPanel />

        <PurgeUsersPanel />

        <PasswordPanel />

        {/* Bulk hide-above control */}
        <BulkHideAbove onDone={load} />

        {fetching && <p className="text-white/50 font-outfit">loading episodes...</p>}

        <div className="space-y-4">
          {(() => {
            const pt2Set = new Set(eps.filter(e => e.part === 2).map(e => e.parent_number));
            return eps.map((ep) => (
              <EpisodeRow key={ep.number} ep={ep} hasPt2={pt2Set.has(ep.number)} onSaved={load} />
            ));
          })()}
        </div>

        <CheckoutFailuresPanel />
      </div>
    </div>
  );
}
